import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, ArrowRight, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { SERVICES } from "../data/services";
import { trackPhoneClick } from "../lib/tracking";

const TOP = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about", children: [
    { label: "About Us", to: "/about" },
    { label: "Our Values", to: "/our-values" },
    { label: "Quality & Compliance", to: "/about#compliance" },
    { label: "Policies", to: "/policies" },
  ]},
  { label: "Services", to: "/services", servicesDropdown: true },
  { label: "Our Homes", to: "/vacancies" },
  { label: "For Participants", to: "/for-participants" },
  { label: "For Families", to: "/for-families" },
  { label: "For Coordinators", to: "/for-support-coordinators" },
  { label: "Resources", to: "/resources", children: [
    { label: "Blog & Guides", to: "/resources" },
    { label: "FAQ", to: "/faq" },
    { label: "Easy Read", to: "/easy-read" },
    { label: "Locations", to: "/locations" },
    { label: "Participant portal", to: "/portal" },
  ]},
  { label: "Contact", to: "/contact" },
];

function Dropdown({ item }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const close = () => setOpen(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        onClick={() => navigate(item.to)}
        className="px-3 py-2 rounded-full text-sm font-medium text-[color:var(--alpha-ink)] hover:text-[color:var(--alpha-purple-700)] hover:bg-[color:var(--alpha-purple-50)] transition-colors inline-flex items-center gap-1"
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 ${item.servicesDropdown ? "w-[560px]" : "w-[260px]"}`}>
          <div className="alpha-card p-4 shadow-xl border border-[color:var(--alpha-line)]" data-testid={`nav-dropdown-${item.label.toLowerCase()}`}>
            {item.servicesDropdown ? (
              <>
                <div className="alpha-overline mb-3 px-2">Our services</div>
                <div className="grid grid-cols-2 gap-1">
                  {SERVICES.map((s) => {
                    const Icon = s.Icon;
                    return (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        onClick={close}
                        className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-[color:var(--alpha-purple-50)] transition-colors group"
                        data-testid={`nav-service-${s.slug}`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center shrink-0 group-hover:bg-[color:var(--alpha-purple-700)] group-hover:text-white transition-colors">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[color:var(--alpha-ink)] truncate">{s.short}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <div className="mt-3 pt-3 border-t border-[color:var(--alpha-line)] flex items-center justify-between px-2">
                  <div className="text-xs text-[color:var(--alpha-mute)]">All services in one place</div>
                  <Link to="/services" onClick={close} className="text-xs font-semibold text-[color:var(--alpha-purple-700)] inline-flex items-center gap-1">View all <ArrowUpRight className="w-3 h-3" /></Link>
                </div>
              </>
            ) : (
              <ul>
                {item.children?.map((c) => (
                  <li key={c.to}>
                    <Link to={c.to} onClick={close} className="block px-3 py-2 text-sm rounded-lg hover:bg-[color:var(--alpha-purple-50)]" data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {c.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const on = () => setScrolled(window.scrollY > 10);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-[color:var(--alpha-bg)]/95 backdrop-blur-xl border-b border-[color:var(--alpha-line)]" : "bg-[color:var(--alpha-bg)]/80 backdrop-blur"
      }`}
    >
      {/* Announcement strip */}
      <div className="bg-[color:var(--alpha-purple-800)] text-[color:var(--alpha-purple-50)] text-xs">
        <div className="alpha-container py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--alpha-green-500)] shrink-0"></span>
            <span className="opacity-95 truncate">Registered NDIS Provider · Servicing Australia</span>
          </div>
          <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "nav-strip")} className="inline-flex items-center gap-1.5 opacity-95 hover:opacity-100 whitespace-nowrap" data-testid="nav-phone">
            <Phone className="w-3.5 h-3.5" /> <span className="hidden xs:inline sm:inline">1300 380 692</span>
          </a>
        </div>
      </div>

      <div className="alpha-container flex items-center justify-between py-4 gap-6">
        <Link to="/" className="flex items-center gap-3 alpha-focus shrink-0" data-testid="nav-logo">
          <img
            src="/brand/logo-green-mark.png"
            alt="Alpha Community Care"
            className="h-11 w-auto md:h-12 select-none"
            draggable="false"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-semibold text-[15px] md:text-[16px] text-[color:var(--alpha-ink)]">Alpha Community Care</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--alpha-mute)]">Registered NDIS Provider</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {TOP.map((n) =>
            n.children || n.servicesDropdown ? (
              <Dropdown key={n.label} item={n} />
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? "text-[color:var(--alpha-purple-800)] bg-[color:var(--alpha-purple-50)]"
                      : "text-[color:var(--alpha-ink)] hover:text-[color:var(--alpha-purple-700)] hover:bg-[color:var(--alpha-purple-50)]"
                  }`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate("/referrals")}
            className="hidden md:inline-flex alpha-btn-primary text-sm"
            data-testid="nav-refer-btn"
          >
            Make a Referral <ArrowRight className="w-4 h-4" />
          </button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="xl:hidden p-2 rounded-lg border border-[color:var(--alpha-line-strong)]" data-testid="nav-menu-btn" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[340px] p-0 bg-[color:var(--alpha-bg)]">
              <div className="p-5 border-b border-[color:var(--alpha-line)] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src="/brand/logo-green-mark.png" alt="Alpha Community Care" className="h-9 w-auto" />
                  <div className="font-display font-semibold">Menu</div>
                </div>
                <button onClick={() => setOpen(false)} aria-label="Close menu"><X className="w-5 h-5" /></button>
              </div>
              <nav className="p-3 flex flex-col overflow-y-auto max-h-[calc(100vh-200px)]">
                {TOP.map((n) => (
                  <div key={n.label}>
                    {n.servicesDropdown ? (
                      <div className="border-b border-[color:var(--alpha-line)] last:border-0">
                        <button
                          onClick={() => setMobileServicesOpen((v) => !v)}
                          className="w-full flex items-center justify-between px-3 py-3 text-base font-semibold"
                          data-testid="mnav-services-toggle"
                        >
                          Services
                          <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                        </button>
                        {mobileServicesOpen && (
                          <div className="pb-3">
                            {SERVICES.map((s) => (
                              <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                onClick={() => setOpen(false)}
                                className="block px-4 py-2.5 text-sm rounded-lg hover:bg-[color:var(--alpha-purple-50)]"
                                data-testid={`mnav-service-${s.slug}`}
                              >
                                {s.short}
                              </Link>
                            ))}
                            <Link to="/services" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-sm font-semibold text-[color:var(--alpha-purple-700)]">
                              View all services →
                            </Link>
                          </div>
                        )}
                      </div>
                    ) : n.children ? (
                      <details className="border-b border-[color:var(--alpha-line)] last:border-0 group">
                        <summary className="px-3 py-3 text-base font-semibold list-none flex items-center justify-between cursor-pointer">
                          {n.label}
                          <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                        </summary>
                        <div className="pb-2">
                          {n.children.map((c) => (
                            <Link
                              key={c.to}
                              to={c.to}
                              onClick={() => setOpen(false)}
                              className="block px-4 py-2 text-sm rounded-lg hover:bg-[color:var(--alpha-purple-50)]"
                              data-testid={`mnav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      </details>
                    ) : (
                      <NavLink
                        to={n.to}
                        end={n.to === "/"}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `block px-3 py-3 text-base font-semibold border-b border-[color:var(--alpha-line)] last:border-0 ${
                            isActive ? "text-[color:var(--alpha-purple-800)]" : "text-[color:var(--alpha-ink)]"
                          }`
                        }
                        data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {n.label}
                      </NavLink>
                    )}
                  </div>
                ))}
              </nav>
              <div className="p-4 border-t border-[color:var(--alpha-line)] space-y-2 bg-[color:var(--alpha-bg-soft)]">
                <Link to="/referrals" onClick={() => setOpen(false)} className="alpha-btn-primary w-full justify-center" data-testid="mnav-refer">
                  Make a Referral
                </Link>
                <Link to="/contact" onClick={() => setOpen(false)} className="alpha-btn-ghost w-full justify-center">
                  Get Support
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
